/**
 * Apple Login Button Component
 *
 * Implements Sign in with Apple for web using Apple's JS SDK.
 * Handles the OAuth flow and stores tokens on successful authentication.
 */

import { useEffect, useState } from 'react';
import { useNotify, useRedirect } from 'react-admin';
import { Box, Button, CircularProgress, Alert } from '@mui/material';
import AppleIcon from '@mui/icons-material/Apple';
import config from '../config';
import Logger from '../utils/Logger';

// Apple's Sign In JS SDK types
declare global {
  interface Window {
    AppleID?: {
      auth: {
        init: (config: AppleAuthConfig) => void;
        signIn: () => Promise<AppleSignInResponse>;
      };
    };
  }
}

interface AppleAuthConfig {
  clientId: string;
  scope: string;
  redirectURI: string;
  usePopup: boolean;
}

interface AppleSignInResponse {
  authorization: {
    code: string;
    id_token: string;
    state?: string;
  };
  user?: {
    email?: string;
    name?: {
      firstName?: string;
      lastName?: string;
    };
  };
}

interface AppleLoginButtonProps {
  onSuccess?: () => void;
  onError?: (error: string) => void;
  /** Callback when MFA setup is required (mfaRequired=true but mfaEnabled=false) */
  onMfaSetupRequired?: () => void;
}

/**
 * Apple Login Button
 *
 * Renders a "Sign in with Apple" button that:
 * 1. Loads Apple's JS SDK
 * 2. Opens Apple sign-in popup
 * 3. Gets authorization code + user info (first time only)
 * 4. Sends to backend /auth/oauth/apple
 * 5. Stores returned JWTs in localStorage
 * 6. Redirects to dashboard on success
 */
export const AppleLoginButton = ({ onSuccess, onError, onMfaSetupRequired }: AppleLoginButtonProps) => {
  const notify = useNotify();
  const redirect = useRedirect();
  const [loading, setLoading] = useState(false);
  const [sdkLoaded, setSdkLoaded] = useState(false);

  const isConfigured = Boolean(config.APPLE_CLIENT_ID);

  // Load Apple's JS SDK
  useEffect(() => {
    if (!isConfigured) return;

    // Check if already loaded
    if (window.AppleID) {
      setSdkLoaded(true);
      return;
    }

    const script = document.createElement('script');
    script.src = 'https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js';
    script.async = true;
    script.onload = () => {
      setSdkLoaded(true);
    };
    script.onerror = () => {
      Logger.error('[AppleLogin] Failed to load Apple JS SDK');
    };
    document.body.appendChild(script);

    return () => {
      // Cleanup if component unmounts before load
      if (script.parentNode) {
        script.parentNode.removeChild(script);
      }
    };
  }, [isConfigured]);

  // Initialize Apple Sign In when SDK is loaded
  useEffect(() => {
    if (!sdkLoaded || !window.AppleID || !isConfigured) return;

    const redirectUri = `${window.location.origin}/auth/apple/callback`;

    window.AppleID.auth.init({
      clientId: config.APPLE_CLIENT_ID,
      scope: 'name email',
      redirectURI: redirectUri,
      usePopup: true,
    });
  }, [sdkLoaded, isConfigured]);

  const handleAppleSignIn = async () => {
    if (!window.AppleID) {
      notify('Apple Sign-In not available', { type: 'error' });
      return;
    }

    setLoading(true);

    try {
      // Open Apple sign-in popup
      const response = await window.AppleID.auth.signIn();

      Logger.log('[AppleLogin] Apple sign-in successful, processing...');

      const redirectUri = `${window.location.origin}/auth/apple/callback`;

      // Send to backend
      const backendResponse = await fetch(`${config.API_URL}/auth/oauth/apple`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          code: response.authorization.code,
          redirectUri: redirectUri,
          user: response.user || null, // User info only on first sign-in
        }),
      });

      if (!backendResponse.ok) {
        const errorData = await backendResponse.json().catch(() => ({}));
        const errorMessage = errorData.message || `Server error: ${backendResponse.status}`;
        Logger.error('[AppleLogin] Backend error:', errorMessage);
        notify(`Sign in failed: ${errorMessage}`, { type: 'error' });
        onError?.(errorMessage);
        return;
      }

      const tokens = await backendResponse.json();

      // Store tokens (same as password login)
      localStorage.setItem('token', tokens.idToken);
      localStorage.setItem('refreshToken', tokens.refreshToken);
      if (tokens.accessToken) {
        localStorage.setItem('accessToken', tokens.accessToken);
      }
      if (tokens.deviceKey) {
        localStorage.setItem('deviceKey', tokens.deviceKey);
      }

      Logger.log('[AppleLogin] Login successful, tokens stored');

      // Check if MFA setup is required (admin forced MFA but user hasn't set it up yet)
      if (tokens.requiresMfaSetup) {
        Logger.log('[AppleLogin] MFA setup required');
        notify('Please set up two-factor authentication to continue', { type: 'info' });
        onMfaSetupRequired?.();
        return;
      }

      notify('Signed in successfully', { type: 'success' });

      onSuccess?.();
      redirect('/');
    } catch (error: any) {
      // User cancelled or error occurred
      if (error?.error === 'popup_closed_by_user') {
        Logger.log('[AppleLogin] User cancelled sign-in');
        notify('Sign-in cancelled', { type: 'info' });
      } else {
        Logger.error('[AppleLogin] Error:', error);
        const message = error?.message || 'Apple sign-in failed';
        notify(`Sign in failed: ${message}`, { type: 'error' });
        onError?.(message);
      }
    } finally {
      setLoading(false);
    }
  };

  // Don't render if not configured
  if (!isConfigured) {
    return null;
  }

  return (
    <Box sx={{ mt: 1.5, width: '100%' }}>
      <Button
        variant="outlined"
        fullWidth
        onClick={handleAppleSignIn}
        disabled={loading || !sdkLoaded}
        startIcon={loading ? <CircularProgress size={20} /> : <AppleIcon />}
        sx={{
          py: 1.2,
          color: '#000',
          borderColor: '#000',
          backgroundColor: '#fff',
          '&:hover': {
            backgroundColor: '#f5f5f5',
            borderColor: '#000',
          },
        }}
      >
        {loading ? 'Signing in...' : 'Sign in with Apple'}
      </Button>
    </Box>
  );
};

export default AppleLoginButton;
